import { createRouter, createWebHashHistory } from 'vue-router';
import { isAuthenticated, currentUser, getUserEffectivePermissions } from '../db/auth.js';
import Swal from 'sweetalert2';
import LandingPage from '../pages/LandingPage.vue';
import UsersPage from '../pages/UsersPage.vue';
import InventoryPage from '../pages/InventoryPage.vue';
import ProductsPage from '../pages/ProductsPage.vue';
import DashboardPage from '../pages/DashboardPage.vue';
import AnalyticsDashboard from '../pages/AnalyticsDashboard.vue';
import LoginPage from '../pages/LoginPage.vue';
import RegisterPage from '../pages/RegisterPage.vue';
import ActivationPage from '../pages/ActivationPage.vue';
import ForgotPasswordPage from '../pages/ForgotPasswordPage.vue';
import ResetPasswordPage from '../pages/ResetPasswordPage.vue';
import PurchasesPage from '../pages/PurchasesPage.vue';
import ProfilePage from '../pages/ProfilePage.vue';
import MenuPage from '../pages/MenuPage.vue';
import SalesPage from '../pages/SalesPage.vue';
import IngredientesPage from '../pages/IngredientesPage.vue';
import TaxesPage from '../pages/TaxesPage.vue';
import InvoicingPage from '../pages/InvoicingPage.vue';
import UpdatesPage from '../pages/UpdatesPage.vue';
import BackupPage from '../pages/BackupPage.vue';
import SuppliersPage from '../pages/SuppliersPage.vue';
import BasePurchasesPage from '../pages/BasePurchasesPage.vue';
import ReportSalesPage from '../pages/ReportSalesPage.vue';
import DailySalesPage from '../pages/DailySalesPage.vue';
import DailyProfitsPage from '../pages/DailyProfitsPage.vue';

const routes = [
  { path: '/', name: 'landing', component: LandingPage },
  { path: '/dashboard', name: 'dashboard', component: DashboardPage, meta: { requiresAuth: true, code: 'dashboard' } },
  { path: '/dashboard/analytics', name: 'dashboard-analytics', component: AnalyticsDashboard, meta: { requiresAuth: true, code: 'dashboard' } },
  { path: '/reportes/ventas', name: 'report-sales', component: ReportSalesPage, meta: { requiresAuth: true, code: 'report_sales' } },
  { path: '/reportes/ventas-diarias', name: 'daily-sales', component: DailySalesPage, meta: { requiresAuth: true, code: 'daily_sales' } },
  { path: '/reportes/ganancias-diarias', name: 'daily-profits', component: DailyProfitsPage, meta: { requiresAuth: true, code: 'daily_profits' } },
  { path: '/inventario', name: 'inventario', component: InventoryPage, meta: { requiresAuth: true, code: 'inventory' } },
  { path: '/productos', name: 'productos', component: ProductsPage, meta: { requiresAuth: true, code: 'products' } },
  { path: '/ventas', name: 'ventas', component: SalesPage, meta: { requiresAuth: true, code: 'sales' } },
  { path: '/compras', name: 'compras', component: PurchasesPage, meta: { requiresAuth: true, code: 'purchases' } },
  { path: '/base-compras', name: 'base-compras', component: BasePurchasesPage, meta: { requiresAuth: true, requiresAdmin: true, code: 'base_purchases' } },
  { path: '/proveedores', name: 'proveedores', component: SuppliersPage, meta: { requiresAuth: true, code: 'suppliers' } },
  { path: '/impuestos', name: 'impuestos', component: TaxesPage, meta: { requiresAuth: true, code: 'taxes' } },
  { path: '/facturacion', name: 'facturacion', component: InvoicingPage, meta: { requiresAuth: true, code: 'invoicing' } },
  { path: '/actualizaciones', name: 'actualizaciones', component: UpdatesPage, meta: { requiresAuth: true, code: 'updates' } },
  { path: '/backup', name: 'backup', component: BackupPage, meta: { requiresAuth: true, code: 'backup' } },
  { path: '/usuarios', name: 'usuarios', component: UsersPage, meta: { requiresAuth: true, requiresAdmin: true, code: 'users' } },
  { path: '/perfil', name: 'perfil', component: ProfilePage, meta: { requiresAuth: true } },
  { path: '/login', name: 'login', component: LoginPage },
  { path: '/register', name: 'register', component: RegisterPage },
  { path: '/activate', name: 'activate', component: ActivationPage },
  { path: '/forgot-password', name: 'forgot-password', component: ForgotPasswordPage },
  { path: '/reset-password', name: 'reset-password', component: ResetPasswordPage },
  // Menú público para visualizar productos
  { path: '/menu', name: 'menu', component: MenuPage },
  { path: '/ingredientes', name: 'ingredientes', component: IngredientesPage, meta: { requiresAuth: true, code: 'initial_inventory' } },
];


export const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

// Protección básica de rutas: si no hay sesión en localStorage, ir a login
router.beforeEach(async (to, from, next) => {
  // Si el usuario está autenticado y va a la landing, redirigir al dashboard
  if (to.path === '/' && await isAuthenticated()) {
    return next({ path: '/dashboard' });
  }
  
  const requires = to.meta?.requiresAuth;
  if (!requires) return next();
  const ok = await isAuthenticated();
  if (!ok) return next({ path: '/login', query: { redirect: to.fullPath } });
  const u = currentUser();
  if (to.meta?.requiresAdmin) {
    const role = (u?.role || '').toLowerCase();
    const isAdmin = ['admin','superadmin'].includes(role) || u?.role_id === 1;
    if (!isAdmin) {
      try {
        await Swal.fire({
          icon: 'info',
          title: 'Acceso bloqueado',
          text: 'No tienes permisos para acceder a este apartado.',
          confirmButtonText: 'Entendido',
        });
      } catch {}
      return next(false);
    }
  }
  // Validar permisos efectivos por módulo si la ruta tiene código
  try {
    const code = to.meta?.code;
    const role = (u?.role || '').toLowerCase();
    if (u && code) {
      if (code === 'dashboard') {
        return next();
      }
      let rows = await getUserEffectivePermissions(u.id);
      let allowedCodes = rows.filter(r => r.can_access === 1).map(r => r.code);
      const allowed = new Set(allowedCodes);
      if (role === 'cliente') {
        ['products','purchases','menu'].forEach(c => allowed.add(c));
      }
      if (!allowed.has(code)) {
        try {
          await Swal.fire({
            icon: 'info',
            title: 'Acceso bloqueado',
            text: 'No tienes permisos para acceder a este apartado.',
            confirmButtonText: 'Entendido',
          });
        } catch {}
        return next({ path: '/dashboard' });
      }
    }
  } catch {}
  next();
});

export default router;
